/**
 * SaveStatusIndicator — Phase 3.86 (Feb 2026)
 *
 * Diskrete Live-Status-Anzeige für die Einstellungs-Sektion (Notion/Linear-Style).
 * Zeigt drei Zustände:
 *  - `saving`  → "Speichert…"           (graue Pille)
 *  - `dirty`   → "Ungespeichert"        (amber Pille mit Punkt)
 *  - `saved`   → "✓ Gespeichert vor Xs" (grün, relative Zeit, tickert alle 5s)
 *
 * Erscheint absichtlich unauffällig — der bestehende Sticky-Banner bleibt der
 * primäre Call-to-Action. Diese Pille beruhigt den Nutzer nach erfolgreichem
 * Save („meine Änderungen sind sicher").
 */
import { useEffect, useState } from "react";
import { Check, CircleDot, Loader2 } from "lucide-react";

function formatRelative(diffSec) {
    if (diffSec < 5) return "gerade eben";
    if (diffSec < 60) return `vor ${diffSec} s`;
    if (diffSec < 3600) return `vor ${Math.round(diffSec / 60)} min`;
    if (diffSec < 86400) return `vor ${Math.round(diffSec / 3600)} h`;
    return `vor ${Math.round(diffSec / 86400)} d`;
}

export default function SaveStatusIndicator({ savedAt, dirty, saving }) {
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (saving || dirty || !savedAt) return undefined;
        const t = setInterval(() => setNow(Date.now()), 5000);
        return () => clearInterval(t);
    }, [saving, dirty, savedAt]);

    if (saving) {
        return (
            <span
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 font-medium"
                data-testid="save-status-indicator-saving"
            >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Speichert…
            </span>
        );
    }
    if (dirty) {
        return (
            <span
                className="inline-flex items-center gap-1.5 text-xs text-amber-700 font-medium"
                data-testid="save-status-indicator-dirty"
            >
                <CircleDot className="w-3.5 h-3.5" />
                Ungespeichert
            </span>
        );
    }
    if (!savedAt) return null;

    const diffSec = Math.max(0, Math.round((now - savedAt) / 1000));
    return (
        <span
            className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium"
            data-testid="save-status-indicator-saved"
            title={new Date(savedAt).toLocaleString("de-DE")}
        >
            <Check className="w-3.5 h-3.5" />
            Gespeichert {formatRelative(diffSec)}
        </span>
    );
}
